import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useRoleGuard } from "@/hooks/use-role-guard";
import { adminGetUserRoles, adminDeleteUser } from "@/lib/admin.functions";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

export const Route = createFileRoute("/admin/subscriptions")({
  head: () => ({
    meta: [
      { title: "Subscriptions - Admin - DeckScore" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SubscriptionsPage,
});

type UserRow = {
  user_id: string;
  email: string;
  full_name?: string | null;
  plan: string;
  effective_plan: string;
  plan_expires_at: string | null;
  expired: boolean;
  granted_at: string | null;
  forms_this_month: number;
  fills_this_month: number;
  total_fills: number;
  last_fill_at: string | null;
};

const DURATIONS = [1, 3, 6, 12] as const;
type Duration = (typeof DURATIONS)[number];

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function withinDays(iso: string | null, days: number): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return false;
  const diff = t - Date.now();
  return diff >= 0 && diff <= days * 24 * 60 * 60 * 1000;
}

function relativeTime(iso: string | null): string {
  if (!iso) return "never";
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "never";
  const diff = Date.now() - t;
  const min = 60_000, hr = 3_600_000, day = 86_400_000;
  if (diff < min) return "just now";
  if (diff < hr) return `${Math.floor(diff / min)}m ago`;
  if (diff < day) return `${Math.floor(diff / hr)}h ago`;
  if (diff < 30 * day) return `${Math.floor(diff / day)}d ago`;
  if (diff < 365 * day) return `${Math.floor(diff / (30 * day))}mo ago`;
  return `${Math.floor(diff / (365 * day))}y ago`;
}

function PlanBadge({ user }: { user: UserRow }) {
  if (user.effective_plan === "pro") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#DDF0DA] text-[#2E5B2E]">
        Pro
      </span>
    );
  }
  if (user.plan === "pro" && user.expired) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#FDE9C7] text-[#8A5A16]">
        Pro (expired)
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-surface-sunken text-ink-tertiary">
      Free
    </span>
  );
}

function SubscriptionsPage() {
  const { status: roleStatus, role: viewerRole } = useRoleGuard(["admin", "super_admin"]);
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<string | null>(null);
  const [durations, setDurations] = useState<Record<string, Duration>>({});
  const [roles, setRoles] = useState<Record<string, string>>({});
  const [joined, setJoined] = useState<Record<string, string | null>>({});
  const [planFilter, setPlanFilter] = useState<"all" | "free" | "pro">("all");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "email">("newest");
  const [viewerId, setViewerId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserRow | null>(null);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [deleting, setDeleting] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const runGetRoles = useServerFn(adminGetUserRoles);
  const runDeleteUser = useServerFn(adminDeleteUser);

  const load = useCallback(async (q: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("list-subscriptions", {
        body: { search: q, limit: 100 },
      });
      if (error) {
        const msg =
          (data as { message?: string } | null)?.message || error.message || "Failed to load.";
        toast.error(msg);
        setUsers([]);
        return;
      }
      const d = data as { ok?: boolean; users?: UserRow[]; message?: string };
      if (d?.ok) {
        const rows = d.users ?? [];
        setUsers(rows);
        try {
          const r = await runGetRoles({ data: { userIds: rows.map((u) => u.user_id) } });
          setRoles(r.roles);
          setJoined(r.joined);
          setViewerId(r.viewerId);
        } catch {
          setRoles({});
          setJoined({});
        }
      } else {
        toast.error(d?.message || "Failed to load.");
        setUsers([]);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load.");
      setUsers([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function canDelete(u: UserRow): boolean {
    if (viewerRole !== "super_admin") return false;
    if (viewerId && u.user_id === viewerId) return false;
    const r = roles[u.user_id];
    return r === "founder" || r === "investor";
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await runDeleteUser({ data: { userId: deleteTarget.user_id } });
      toast.success(`Deleted ${res.email}`);
      setDeleteTarget(null);
      setConfirmEmail("");
      await load(search.trim());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not delete this account.");
    } finally {
      setDeleting(false);
    }
  }

  useEffect(() => {
    if (roleStatus !== "ok") return;
    load("");
  }, [roleStatus, load]);

  // Debounced search
  useEffect(() => {
    if (roleStatus !== "ok") return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      load(search.trim());
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  async function runAction(u: UserRow, action: "grant" | "revoke") {
    setPending(u.user_id);
    try {
      const body: Record<string, unknown> = { user_id: u.user_id, action };
      if (action === "grant") body.months = durations[u.user_id] ?? 1;
      const { data, error } = await supabase.functions.invoke("grant-subscription", { body });
      if (error) {
        const msg =
          (data as { message?: string } | null)?.message ||
          error.message ||
          "Something went wrong.";
        toast.error(msg);
        return;
      }
      const d = data as {
        ok?: boolean;
        email?: string;
        plan_expires_at?: string | null;
        message?: string;
      };
      if (d?.ok) {
        if (action === "grant") {
          const until = d.plan_expires_at ? ` until ${formatDate(d.plan_expires_at)}` : "";
          toast.success(`Granted Pro to ${d.email ?? u.email}${until}`);
        } else {
          toast.success(`Revoked Pro for ${d.email ?? u.email}`);
        }
        await load(search.trim());
      } else {
        toast.error(d?.message || "Something went wrong.");
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Something went wrong.");
    } finally {
      setPending(null);
    }
  }

  if (roleStatus !== "ok") return null;

  const joinedAt = (u: UserRow): number => {
    const iso = joined[u.user_id] ?? null;
    const t = iso ? new Date(iso).getTime() : NaN;
    return isNaN(t) ? 0 : t;
  };

  const visible = users
    .filter((u) => {
      if (planFilter === "all") return true;
      const isPro = u.effective_plan === "pro";
      return planFilter === "pro" ? isPro : !isPro;
    })
    .slice()
    .sort((a, b) => {
      if (sortBy === "email") return a.email.localeCompare(b.email);
      if (sortBy === "oldest") return joinedAt(a) - joinedAt(b);
      return joinedAt(b) - joinedAt(a);
    });

  return (
    <>
      <div className="mb-6">
        <p className="font-script text-accent text-[22px] leading-none">subscriptions</p>
        <h1 className="font-display text-[28px] font-semibold mt-2">Manage Pro users</h1>
        <p className="mt-2 text-[13px] text-ink-secondary">
          Grant, extend or revoke extension Pro plans. Engagement is per calendar month.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by email…"
          className="w-full sm:w-[320px] rounded-full border border-line bg-bg px-4 py-2.5 text-[14px] text-ink placeholder:text-ink-tertiary focus:outline-none focus:border-accent"
        />
        <Select value={planFilter} onValueChange={(v) => setPlanFilter(v as typeof planFilter)}>
          <SelectTrigger className="h-9 text-[13px] w-[130px]">
            <SelectValue placeholder="Plan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All plans</SelectItem>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="pro">Pro</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
          <SelectTrigger className="h-9 text-[13px] w-[160px]">
            <SelectValue placeholder="Sort" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest first</SelectItem>
            <SelectItem value="oldest">Oldest first</SelectItem>
            <SelectItem value="email">Email A-Z</SelectItem>
          </SelectContent>
        </Select>
        {loading && <span className="text-[13px] text-ink-tertiary">Loading…</span>}
      </div>

      <div className="bg-surface border border-line rounded-2xl overflow-hidden">
        <table className="w-full text-[14px]">
          <thead className="bg-surface-sunken text-ink-secondary">
            <tr>
              <th className="text-left px-4 py-3 font-medium">User</th>
              <th className="text-left px-4 py-3 font-medium">Plan</th>
              <th className="text-left px-4 py-3 font-medium">Joined</th>
              <th className="text-left px-4 py-3 font-medium">Expires</th>
              <th className="text-left px-4 py-3 font-medium">Engagement</th>
              <th className="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && visible.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-ink-tertiary">
                  Loading…
                </td>
              </tr>
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-ink-tertiary">
                  No users found
                </td>
              </tr>
            ) : (
              visible.map((u) => {
                const isPro = u.effective_plan === "pro";
                const expiresSoon = isPro && withinDays(u.plan_expires_at, 7);
                const dur = durations[u.user_id] ?? 1;
                return (
                  <tr key={u.user_id} className="border-t border-line align-top">
                    <td className="px-4 py-3">
                      <div className="font-medium">{u.email}</div>
                      {u.full_name && (
                        <div className="text-[12px] text-ink-tertiary">{u.full_name}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <PlanBadge user={u} />
                    </td>
                    <td className="px-4 py-3 text-ink-secondary">
                      {formatDate(joined[u.user_id] ?? null)}
                    </td>
                    <td className={`px-4 py-3 ${expiresSoon ? "text-[#8A5A16]" : "text-ink-secondary"}`}>
                      {formatDate(u.plan_expires_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div>{u.forms_this_month} forms this mo</div>
                      <div className="text-[12px] text-ink-tertiary">
                        {u.total_fills} total · last {relativeTime(u.last_fill_at)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap justify-end items-center gap-2">
                        <Select
                          value={String(dur)}
                          onValueChange={(v) =>
                            setDurations((prev) => ({
                              ...prev,
                              [u.user_id]: Number(v) as Duration,
                            }))
                          }
                          disabled={pending === u.user_id}
                        >
                          <SelectTrigger className="h-8 text-[13px] w-[110px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DURATIONS.map((m) => (
                              <SelectItem key={m} value={String(m)}>
                                {m} month{m === 1 ? "" : "s"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <button
                          type="button"
                          onClick={() => runAction(u, "grant")}
                          disabled={pending === u.user_id}
                          className="inline-flex h-8 items-center justify-center rounded-full bg-accent px-3 text-[12.5px] font-bold text-accent-ink hover:bg-accent-press disabled:opacity-60"
                        >
                          {isPro ? "Extend" : "Grant Pro"}
                        </button>
                        {isPro && (
                          <button
                            type="button"
                            onClick={() => runAction(u, "revoke")}
                            disabled={pending === u.user_id}
                            className="inline-flex h-8 items-center justify-center rounded-full border border-line bg-surface px-3 text-[12.5px] font-semibold text-ink hover:bg-surface-sunken disabled:opacity-60"
                          >
                            Revoke
                          </button>
                        )}
                        {canDelete(u) && (
                          <button
                            type="button"
                            onClick={() => {
                              setDeleteTarget(u);
                              setConfirmEmail("");
                            }}
                            disabled={pending === u.user_id}
                            className="inline-flex h-8 items-center justify-center rounded-full border border-destructive/40 bg-surface px-3 text-[12.5px] font-semibold text-destructive hover:bg-destructive/10 disabled:opacity-60"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open && !deleting) {
            setDeleteTarget(null);
            setConfirmEmail("");
          }
        }}
      >
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Delete {deleteTarget?.email}</DialogTitle>
            <DialogDescription>
              This permanently removes their login and everything they have on DeckScore - decks,
              orders and analyses. It cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="block text-[13px] text-ink-secondary">
              Type the email to confirm
            </label>
            <input
              type="text"
              autoComplete="off"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder={deleteTarget?.email ?? ""}
              className="w-full rounded-lg border border-line bg-bg px-3 py-2 text-[14px] text-ink placeholder:text-ink-tertiary focus:outline-none focus:border-accent"
            />
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => {
                setDeleteTarget(null);
                setConfirmEmail("");
              }}
              disabled={deleting}
              className="inline-flex h-9 items-center justify-center rounded-full border border-line bg-surface px-4 text-[13px] font-semibold text-ink hover:bg-surface-sunken disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmDelete}
              disabled={
                deleting ||
                !deleteTarget ||
                confirmEmail.trim().toLowerCase() !== deleteTarget.email.toLowerCase()
              }
              className="inline-flex h-9 items-center justify-center rounded-full bg-destructive px-4 text-[13px] font-bold text-destructive-foreground hover:opacity-90 disabled:opacity-50"
            >
              {deleting ? "Deleting..." : "Delete permanently"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
